import { useNavigate } from 'react-router-dom';
import { ShopProductCard } from '@/components/shop/ShopProductCard';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import type { ProductCard } from '@/types/shop';
import { SectionHeader } from '../shared/SectionHeader';
import { useWishlist } from '@/hooks/useWishlist';

const transformWishlistToProductCard = (wishlistItem: any): ProductCard => {
  const product = wishlistItem.product || wishlistItem;
  const seller = product.seller;
  
  return {
    id: product._id || product.id,
    title: product.name,
    description: product.description,
    price: product.discountedPrice || product.price,
    originalPrice: product.price,
    discountPercentage: product.price > product.discountedPrice 
      ? Math.round(((product.price - product.discountedPrice) / product.price) * 100)
      : 0,
    image: product.images?.[0]?.url || product.images?.[0]?.originalUrl || '',
    category: product.category,
    subCategory: product.subCategory || '',
    brand: product.brand || '',
    sellerId: seller?._id || '',
    sellerName: seller?.storeName || `${seller?.firstName || ''} ${seller?.lastName || ''}`.trim() || 'Seller',
    location: seller?.warehouseDetails?.address?.city 
      ? `${seller.warehouseDetails.address.city}, ${seller.warehouseDetails.address.state}`.toLowerCase()
      : 'nigeria',
    stock: product.stock || 0,
    hasProtection: true, 
    hasVariants: product.variants && product.variants.length > 0,
    variants: product.variants || [],
    weight: product.shippingInfo?.weight?.value || 1.0,
    sku: product.sku || '',
    upc: product.upc || '',
    condition: product.condition || 'new',
  };
};

export default function WishlistPage() {
  const navigate = useNavigate();

  // Use the same hook as everywhere else in the app
  const { wishlistFullData, isLoading } = useWishlist();

  const handleProductClick = (productId: string) => {
    navigate(`/products/${productId}`);
  };

  // Transform wishlist data to ProductCard format
  const products: ProductCard[] = (wishlistFullData || []).map(transformWishlistToProductCard);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  // Empty State
  if (products.length === 0) {
    return (
      <div className="w-full min-h-screen bg-[#F4F4F4] px-4 py-4">
        <div className="max-w-7xl mx-auto px-4 py-4 min-h-screen rounded-xs bg-white">
          <SectionHeader title="My Wishlist" />
          <div className="flex flex-col items-center justify-center py-16">
            <div className="text-center max-w-md">
              <svg
                className="mx-auto h-24 w-24 text-gray-300 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
              <h3 className="text-xl font-lexend font-medium text-gray-900 mb-2">
                Your wishlist is empty
              </h3>
              <p className="text-gray-600 mb-6 font-lexend">
                Save items you love by clicking the heart icon on products
              </p>
              <button
                onClick={() => navigate('/shop')}
                className="bg-primary-green text-white px-6 py-3 rounded-md hover:bg-black transition-colors font-lexend font-medium"
              >
                Start Shopping
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-[#F4F4F4] px-4 py-4">
      <div className="max-w-7xl mx-auto px-4 py-4 min-h-screen rounded-xs bg-white">
        <SectionHeader title="My Wishlist" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 scrollbar-hide pb-2">
          {products.map((product) => (
            <ShopProductCard
              key={product.id}
              product={product}
              onClick={() => handleProductClick(product.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}